<template>
  <div class="columns upload-wrapper is-gapless">
    <div class="column document-library is-half" v-if="canPickFromLib">
      <document-library v-model="libraryDocuments" ref="document-library" />
    </div>

    <div class="column column-file-list" :class="{ 'is-half': canPickFromLib }">
      <drag-drop-upload :multiple="true"
                        @input-file="handleDragDropFile"
                        :drop="true"
                        class="file-upload-drag-drop-zone" />

      <ul class="file-list">
        <li>
          <p class="is-size-7 has-text-centered">Maximum file size is 4.7mb</p>
        </li>
        <li>
          <p class="is-size-7 has-text-centered" v-show="files.length" v-if="canPickFromLib">Toggle the switch to add document to library</p>
        </li>
        <!-- TODO: turn this in to a component to not have to deal with `index` all the time -->
        <!--<li v-for="(file, index) in files" :key="file.name" class="file-row" v-model="file"> -->
          <document-uploader-row v-for="(file, index) in files"
                                 :key="file.name"
                                 class="file-row"
                                 v-model="files[index]"
                                 @delete="remove(index)"
                                 @can-upload="trackUploadReadiness(index, $event)"
                                 @duplicate="updateDuplicateFiles"
                                 @upload-done="updateDuplicateFiles"
                                 ref="file-row"
                                 :target-document-holder-key="targetDocumentHolderKey"
                                 :target-document-holder-name="targetDocumentHolderName"
                                 :can-add-to-company-lib="canAddToCompanyLib" />

      </ul>
      <div class="buttons has-addons is-fullwidth">
        <b-upload v-model="files" class="button is-success" multiple>
          <font-awesome-icon icon="plus-circle" />
          Add New Documents
        </b-upload>
        <button class="button is-primary" type="button" @click="upload" :disabled="!canUpload">Upload</button>
      </div>
    </div>

    <div class="modal modal-duplicate-warning" :class="{ 'is-active': showDuplicateModal }" v-if="showDuplicateModal">
      <div class="modal-background"></div>
      <div class="modal-card">
        <section class="modal-card-body">
          <p class="content">
            The following files are already in your document library. If you continue they will be over-written with a new version.
            <strong>Would you like to continue?</strong>

            <ul class="list">
              <li v-for="file in duplicateFiles" :key="file.uuid">
                {{file.name}}
              </li>
            </ul>
          </p>

        </section>
        <footer class="modal-card-foot columns is-centered">

          <div class="column is-half">
            <button class="button is-fullwidth" @click="toggleDuplicateWarning">No</button>
          </div>
          <div class="column is-half">
            <button class="button is-fullwidth is-success" @click="_upload">Yes</button>
          </div>

        </footer>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import DocumentsApi from '@/api-interface/document'
import { difference, compact, isEmpty } from 'lodash-es'

import DragDropUpload from 'vue-upload-component'
import DocumentUploaderRow from '@/components/DocumentUploader-row'

import DocumentLibrary from '@/components/DocumentUploader-PickFromLib'
export default {
  name: 'DocumentUploader',

  props: {
    canPickFromLib: {
      type: Boolean,
      default: true
    },
    companyJobKey: {
      type: String
    },
    freightKey: {
      type: String
    },
    companyKey: {
      type: String
    },
    value: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      duplicateFiles: [],
      showDuplicateModal: false,
      files: [],
      isReadyForUpload: [],
      selectedDocuments: [],
      customFileNames: [],
      category: [],
      libraryDocuments: [],
      fromLibrary: [],
      showLibrary: false
    }
  },

  created() {
    this.selectedDocuments = [...this.value]
    this.libraryDocuments = this.selectedDocuments.filter((doc) => doc.documentHolder === 'company')
    this.files = this.selectedDocuments.filter((doc) => doc.documentHolder !== 'company')
  },

  computed: {
    ...mapGetters('user', ['accountInfo']),

    targetDocumentHolderName() {
      let holderName
      if (this.companyKey) {
        holderName = 'company'
      } else if (this.companyJobKey) {
        holderName = 'companyJob'
      } else if (this.freightKey) {
        holderName = 'companyJobFreight'
      }

      return holderName
    },

    targetDocumentHolderKey() {
      return this.companyJobKey || this.freightKey || this.companyKey
    },

    /**
     * Flag to indicate if there is anything to upload
     * @returns {Boolean} True if there is a new file, otherwise false
     */
    canUpload() {
      return this.files.length && compact(this.isReadyForUpload).length === this.files.length
    },

    canAddToCompanyLib() {
      return isEmpty(this.companyKey)
    }
  },

  components: {
    DocumentLibrary,
    DragDropUpload,
    DocumentUploaderRow
  },

  methods: {
    ...mapActions('companyDocuments', { fetchLibraryDocuments: 'fetch' }),

    toggleDuplicateWarning() {
      this.showDuplicateModal = !this.showDuplicateModal
    },

    updateDuplicateFiles(fileInfo) {
      const targetIndex = this.duplicateFiles.findIndex((file) => file.uuid === fileInfo.uuid)

      if (fileInfo.isPresent && targetIndex === -1) {
        this.duplicateFiles.push(fileInfo)
      } else {
        this.duplicateFiles.splice(targetIndex, 1)
      }
    },

    trackUploadReadiness(index, canUpload) {
      // ref: https://vuejs.org/v2/guide/list.html#Caveats
      this.$set(this.isReadyForUpload, index, canUpload)
    },

    /**
     * Remove a file to be uploaded
     * @param {Number} index Position in the list
     */
    remove(index) {
      this.files.splice(index, 1)
      this.isReadyForUpload.splice(index, 1)
    },

    /**
     * The `vue-upload-component` component wraps files in its own object, so we need to unpack just the "raw" file
     * and ignore the rest of the decorating object.
     */
    handleDragDropFile(newFile) {
      if (newFile) {
        this.files.push(newFile.file)
      }
    },

    async upload() {
      if (this.duplicateFiles.length) {
        this.toggleDuplicateWarning()
      } else {
        return this._upload()
      }
    },

    /**
     * Perform the upload.
     * Will skip any files tht were previously uploaded
     * @returns {Promise<void>}
     */
    async _upload() {
      this.showDuplicateModal = false
      await Promise.all(
        this.$refs['file-row'].map((file) => {
          try {
            return file.upload()
          } catch (error) {
            // do nothing, just make sure a failed upload does not cause `Promise.all` to abort
          }
        })
      )

      window.setTimeout(() => {
        // give the document-copy service a chance to copy the docs before pulling the new list
        this.fetchLibraryDocuments()
        this.$emit('documentAdded')
      }, 500)
    },

    async copyLibraryDocs(newDocument) {
      return DocumentsApi.copy({
        documentKey: newDocument.documentKey,
        copyToDocumentHolderKey: this.targetDocumentHolderKey,
        copyToDocumentHolder: 'companyJob'
      })
    },

    /**
     * Notify parent component about change in document selection.
     */
    notify() {
      const combinedDocumentList = [...this.selectedDocuments, ...this.fromLibrary]
      // const documentKeys = combinedDocumentList.map(doc => doc.documentKey)

      this.$emit('input', combinedDocumentList)
      this.$emit('documentAdded', combinedDocumentList)
    }
  },

  watch: {
    selectedDocuments: 'notify',
    //libraryDocuments: 'notify',
    async libraryDocuments(newDocs, oldDocs) {
      const delta = difference(newDocs, oldDocs)

      if (newDocs.length > oldDocs.length) {
        // assume always one
        const newUpload = await this.copyLibraryDocs(delta[0])
        this.fromLibrary.push(newUpload)
        this.notify()
      }
    }
    // files: 'extractLatestFileName'
  }
}
</script>

<style scoped lang="scss">
.modal-duplicate-warning {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  .modal-background {
    background-color: rgba(10, 10, 10, 0.6);
  }

  .modal-card {
    width: 90%;
    height: 90%;
  }

  .modal-card-foot {
    padding: 0 10px 10px 10px;
  }
}

.upload-wrapper {
  height: 300px;
  position: relative;
  background-color: white;
}

.file-upload-drag-drop-zone {
  display: none;
}

.column-file-list {
  max-width: 100%;
}

.document-library {
  font-size: 0.8rem;
  border: 1px solid #eeeeee;
  padding: 0 10px;
  height: 100%;
}

.file-list {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  height: 88%;
  overflow: auto;
  background: white;

  font-size: 0.8rem;
  border: 1px solid #eeeeee;
  padding: 0 10px;
}

.buttons {
  display: flex;
  &:last-child {
    margin: 0;
  }
  .button {
    margin: 0;
    flex: 1;
    border-radius: 0;

    &:first-child {
      border-right: 0;
    }
  }
}
</style>
